import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import EmailVerificationNotice from "./pages/EmailVerificationNotice";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyEmailChange from "./pages/VerifyEmailChange";
import "./App.css";
import CreateContent from "./pages/CreateContent";
import CreateContentForm from "./pages/CreateContentForm";
import CreateLesson from "./pages/CreateLesson";
import CreateFlashcard from "./pages/CreateFlashcard";
import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";
import SuperAdminStats from "./pages/SuperAdminStats";
import AdminContent from "./pages/AdminContent";
import UserSpace from "./pages/UserSpace";
import MySpace from "./pages/MySpace";
import Communities from "./pages/Communities";
import CommunityDetails from "./pages/CommunityDetails";
import CommunityBoard from "./pages/CommunityBoard";
import CommunityLesson from "./pages/CommunityLesson";
import CommunityPublicProfile from "./pages/CommunityPublicProfile";
import DiscoverCommunities from "./pages/DiscoverCommunities";
import DiscoverPeople from "./pages/DiscoverPeople";
import Inbox from "./pages/Inbox";
import Connections from "./pages/Connections";
import ConnectionDetails from "./pages/ConnectionDetails";

import UserFolders from "./pages/UserFolders";
import Profile from "./pages/Profile";
import PublicProfile from "./pages/PublicProfile";
import UserPublicProfile from "./pages/UserPublicProfile";
import Plans from "./pages/Plans";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import Spinner from "./components/Spinner";
import PrivateRoute from "./components/PrivateRoute";
import PlanIntroModal from "./components/PlanIntroModal";
import NotFound from "./components/NotFound";
import { ThemeProvider } from "./context/ThemeContext.jsx";
import { ReadingProvider } from "./context/ReadingContext.jsx";
import { FoldersProvider } from "./context/FoldersContext.jsx";
import TimerExpiredOverlay from "./components/TimerExpiredOverlay";
import StudyButtons from "./components/StudyButtons";
import RouteScrollReset from "./components/RouteScrollReset";
import SeoHead from "./components/SeoHead";
import StudyNotes from "./pages/StudyNotes";
import StudyFlashcardsView from "./pages/StudyFlashcardsView";
import StudyLessons from "./pages/StudyLessons";
import ViewLesson from "./pages/ViewLesson";
import AllLessons from "./pages/AllLessons";
import AllNotes from "./pages/AllNotes";
import AllFlashcards from "./pages/AllFlashcards";
import PublicNotes from "./pages/PublicNotes";
import PublicFlashcards from "./pages/PublicFlashcards";
import SharedLesson from "./pages/SharedLesson";
import Tutorial from "./pages/Tutorial";
import TutorialLesson from "./pages/TutorialLesson";
import TutorialSubjects from "./pages/TutorialSubjects";
import TutorialNotesRedirect from "./pages/TutorialNotesRedirect";
import TutorialFlashcardsRedirect from "./pages/TutorialFlashcardsRedirect";
import ReportBugButton from "./components/ReportBugButton";
import { shouldShowSuperAdminStats } from "./utils/superAdminStatsAccess";

function App() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const showSuperAdminStats = shouldShowSuperAdminStats();
  const isCommunityPublicProfileRoute =
    /^\/communities\/[^/]+\/public-(profile|view)$/.test(location.pathname);
  const hideHeader =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname.startsWith("/study/") ||
    location.pathname.startsWith("/content-") ||
    location.pathname.startsWith("/view/") ||
    location.pathname.startsWith("/public/notes/") ||
    location.pathname.startsWith("/public/flashcards/") ||
    location.pathname.startsWith("/shared/") ||
    (location.pathname.startsWith("/communities") &&
      !isCommunityPublicProfileRoute) ||
    location.pathname.startsWith("/discover-communities") ||
    location.pathname.startsWith("/meet-people") ||
    location.pathname.startsWith("/connections") ||
    location.pathname.startsWith("/inbox") ||
    location.pathname.startsWith("/community/") ||
    location.pathname === "/all-lessons" ||
    location.pathname === "/all-notes" ||
    location.pathname === "/all-flashcards" ||
    location.pathname === "/myspace" ||
    location.pathname === "/userfolders" ||
    location.pathname === "/create-content";
  const hideFooter =
    hideHeader ||
    location.pathname.startsWith("/tutorial/lesson") ||
    location.pathname.startsWith("/public/lesson/") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/superadmin");
  const skipInitialSpinner =
    location.pathname === "/" || location.pathname === "/tutorial";

  useEffect(() => {
    setLoading(false);
  }, []);

  if (
    loading &&
    !skipInitialSpinner &&
    location.pathname !== "/terms" &&
    location.pathname !== "/privacy" &&
    location.pathname !== "/contact"
  ) {
    return <Spinner visible={true} />;
  }

  return (
    <ThemeProvider>
      <ReadingProvider>
        <FoldersProvider>
        <TimerExpiredOverlay />
        <StudyButtons />
        {!loading && <ReportBugButton />}

        <div className="app-app">
          <SeoHead />
          <RouteScrollReset />
          {!hideHeader && <Header />}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/email-verification-notice"
              element={<EmailVerificationNotice />}
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/verify-email-change"
              element={<VerifyEmailChange />}
            />
            <Route path="/" element={<Tutorial />} />
            <Route path="/tutorial" element={<Tutorial />} />
            <Route path="/tutorial/subjects" element={<TutorialSubjects />} />
            <Route path="/tutorial/lesson" element={<TutorialLesson />} />
            <Route path="/tutorial/notes" element={<TutorialNotesRedirect />} />
            <Route
              path="/tutorial/flashcards"
              element={<TutorialFlashcardsRedirect />}
            />
            <Route path="/public/lesson/:lessonId" element={<TutorialLesson />} />
            <Route path="/public/notes/:lessonId" element={<PublicNotes />} />
            <Route path="/public/flashcards/:lessonId" element={<PublicFlashcards />} />
            <Route path="/shared/:token" element={<SharedLesson />} />
            <Route
              path="/community/:communityId/lesson/:lessonId"
              element={
                <PrivateRoute requiredRole="user">
                  <CommunityLesson />
                </PrivateRoute>
              }
            />
            <Route
              path="/community/:communityId/notes/:lessonId"
              element={
                <PrivateRoute requiredRole="user">
                  <PublicNotes />
                </PrivateRoute>
              }
            />
            <Route
              path="/community/:communityId/flashcards/:lessonId"
              element={
                <PrivateRoute requiredRole="user">
                  <PublicFlashcards />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-content"
              element={
                <PrivateRoute requiredRole="user">
                  <CreateContent />
                </PrivateRoute>
              }
            />
            <Route
              path="/content-note"
              element={
                <PrivateRoute requiredRole="user">
                  <CreateContentForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/content-lesson"
              element={
                <PrivateRoute requiredRole="user">
                  <CreateLesson />
                </PrivateRoute>
              }
            />
            <Route
              path="/content-flashcard"
              element={
                <PrivateRoute requiredRole="user">
                  <CreateFlashcard />
                </PrivateRoute>
              }
            />
            <Route
              path="/userfolders"
              element={
                <PrivateRoute requiredRole="user">
                  <UserFolders />
                </PrivateRoute>
              }
            />
            <Route
              path="/userspace"
              element={
                <PrivateRoute requiredRole="user">
                  <UserSpace />
                </PrivateRoute>
              }
            />
            <Route
              path="/myspace"
              element={
                <PrivateRoute requiredRole="user">
                  <MySpace />
                </PrivateRoute>
              }
            />
            <Route
              path="/communities"
              element={
                <PrivateRoute requiredRole="user">
                  <Communities />
                </PrivateRoute>
              }
            />
            <Route
              path="/communities/:communityId/public-profile"
              element={<CommunityPublicProfile mode="manage" />}
            />
            <Route
              path="/communities/:communityId/public-view"
              element={<CommunityPublicProfile mode="view" />}
            />
            <Route
              path="/communities/:communityId"
              element={
                <PrivateRoute requiredRole="user">
                  <CommunityDetails />
                </PrivateRoute>
              }
            />
            <Route
              path="/communities/:communityId/board"
              element={
                <PrivateRoute requiredRole="user">
                  <CommunityBoard />
                </PrivateRoute>
              }
            />
            <Route
              path="/discover-communities"
              element={
                <PrivateRoute requiredRole="user">
                  <DiscoverCommunities />
                </PrivateRoute>
              }
            />
            <Route
              path="/meet-people"
              element={
                <PrivateRoute requiredRole="user">
                  <DiscoverPeople />
                </PrivateRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <PrivateRoute requiredRole="user">
                  <Inbox />
                </PrivateRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <PrivateRoute requiredRole="user">
                  <Connections />
                </PrivateRoute>
              }
            />
            <Route
              path="/connections/:connectionId"
              element={
                <PrivateRoute requiredRole="user">
                  <ConnectionDetails />
                </PrivateRoute>
              }
            />

            <Route
              path="/all-lessons"
              element={
                <PrivateRoute requiredRole="user">
                  <AllLessons />
                </PrivateRoute>
              }
            />
            <Route
              path="/all-notes"
              element={
                <PrivateRoute requiredRole="user">
                  <AllNotes />
                </PrivateRoute>
              }
            />
            <Route
              path="/all-flashcards"
              element={
                <PrivateRoute requiredRole="user">
                  <AllFlashcards />
                </PrivateRoute>
              }
            />
            <Route
              path="/study/notes"
              element={
                <PrivateRoute requiredRole="user">
                  <StudyNotes />
                </PrivateRoute>
              }
            />
            <Route
              path="/study/flashcards"
              element={
                <PrivateRoute requiredRole="user">
                  <StudyFlashcardsView />
                </PrivateRoute>
              }
            />
            <Route
              path="/study/lessons"
              element={
                <PrivateRoute requiredRole="user">
                  <StudyLessons />
                </PrivateRoute>
              }
            />
            <Route
              path="/view/lesson/:id"
              element={
                <PrivateRoute requiredRole="user">
                  <ViewLesson />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/public-profile"
              element={
                <PrivateRoute requiredRole="user">
                  <PublicProfile />
                </PrivateRoute>
              }
            />
            <Route path="/users/:userId" element={<UserPublicProfile />} />
            <Route
              path="/plans"
              element={
                <PrivateRoute requiredRole="user">
                  <Plans />
                </PrivateRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/admin/content"
              element={
                <PrivateRoute requiredRole="admin">
                  <AdminContent />
                </PrivateRoute>
              }
            />

            <Route path="/users" element={<Navigate to="/admin" replace />} />
            <Route
              path="/admin"
              element={
                <PrivateRoute requiredRole="admin">
                  <Admin />
                </PrivateRoute>
              }
            />
            <Route
              path="/superadmin"
              element={
                <PrivateRoute requiredRole="superadmin">
                  <SuperAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/superadmin/stats"
              element={
                <PrivateRoute requiredRole="superadmin">
                  {showSuperAdminStats ? (
                    <SuperAdminStats />
                  ) : (
                    <Navigate to="/superadmin" replace />
                  )}
                </PrivateRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <PlanIntroModal />
          {!hideFooter && <Footer />}
        </div>
        </FoldersProvider>
      </ReadingProvider>
    </ThemeProvider>
  );
}

export default App;
